// --- React and Redux
import React from 'react';
import {useSelector} from 'react-redux';
// --- Actions and Hooks
import {useClearanceMemoTitlesActions} from '../../store/clearanceMemoTitles/useClearanceMemoTitles';
import {useClearanceMemoTitlesDialog} from '../../components/dialogs/dialogs/clearanceMemoTitles';
import {useLoading} from '../../utils/hooks/useLoading';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
import {Col, Row} from 'antd';
import {Pagination} from '../../components/Pagination';
import {ClearanceMemoTitlesFilterForm} from './components/filter-form';
import {ClearanceMemoTitlesGrid} from './components/grid';
import {TwoRowsLayout} from './layout/two-row';
// --- Utilities
import {getItemRange} from '../../utils/pagination';
import {filterCriteria} from '../../utils/filterCriteria';
import {exportToCsv} from '../../utils/exportToCsv';
// --- API
import {datastageApi} from '../../api';
import {usePermissions} from '../../utils/permissions';
import ToastNotificationProvider from "../../components/ToastNotifications";
import {tracker} from '../../analytics/snowplow';
import { getCurrentUser } from '../../utils/localStorageService';
/**
 * ClearanceMemoTitlesPage Component
 *
 * This component renders the Clearance Memo Titles page, which includes:
 * - A filter form for filtering clearance memo titles.
 * - A grid displaying the list of clearance memo titles.
 * - Pagination for navigating through the titles.
 * - Buttons for creating new titles and exporting existing titles.
 *
 * @component
 * @returns {JSX.Element} The rendered Clearance Memo Titles page.
 */
export const ClearanceMemoTitlesPage = () => {
    React.useEffect(() => {        
        var currentUser = getCurrentUser();        
        tracker.setUserId(currentUser);
        tracker.trackPageView({ title: 'Clearance Memo Titles' });
    }, []);
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateClearanceMemoTitles} = useClearanceMemoTitlesActions();
    const {
        legalStatusOptions,
        totalPages,
        currentPage,
        filterForm,
        titleRange,
        titleSort,
        itemsPerPage,
        totalItems,
    } = useSelector((s) => s.titles);
    const {openClearanceMemoTitlesDialog} = useClearanceMemoTitlesDialog();
    const {isLegalAdmin, isLegalUser, isProductCommentsAdmin} = usePermissions();

    // Fetch clearance memo titles from the API
    const fetchClearanceMemoTitles = async () => {
        try {
            return await datastageApi.clearanceMemoTitles(
                filterCriteria(filterForm, '', 'clearanceMemoTitles'),
                '0,999',
                titleSort,
            );
        } catch (error) {
            console.error('Error fetching clearance memo titles:', error);
            return null;
        }
    };

    // Handle filtering of clearance memo titles
    const handleChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        setLoadingTrue();
        try {
            const nextPageRange = getItemRange(pageNumber, itemsPerPage, totalItems);
            const data = await datastageApi.clearanceMemoTitles(
                filterCriteria(filterForm, '', 'clearanceMemoTitles'),
                nextPageRange,
                titleSort,
            );

            // Map legal status IDs to their corresponding labels
            const updatedData = data?.content?.map((item) => ({
                ...item,
                legalStatusId:
                    legalStatusOptions.find((lookup) => lookup.value === item?.legalStatusId)
                        ?.label || null,
            }));

            updateClearanceMemoTitles({
                titles: updatedData,
                totalPages: data?.totalPages,
                currentPage: data?.pageable.pageNumber,
                titleRange: nextPageRange,
                totalItems: data?.totalElements,
            });
        } catch (error) {
            console.error('Error fetching clearance memo titles:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle exporting clearance memo titles to CSV
    const handleExport = async () => {
        setLoadingTrue();
        try {
            const data = await fetchClearanceMemoTitles();
            exportToCsv(data.content, '', 'clearanceMemo_titles');
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle creating a new clearance memo title
    const handleCreateTitle = () => {
        openClearanceMemoTitlesDialog(async (newTitle) => {
            setLoadingTrue();
            try {
                await datastageApi.createClearanceMemoTitle(newTitle);
                const data = await datastageApi.clearanceMemoTitles(
                    filterCriteria(filterForm, '', 'clearanceMemoTitles'),
                    titleRange,
                    titleSort,
                );

                // Map legal status IDs to their corresponding labels
                const updatedData = data?.content?.map((item) => ({
                    ...item,
                    legalStatusId:
                        legalStatusOptions.find((lookup) => lookup.value === item?.legalStatusId)
                            ?.label || null,
                }));

                updateClearanceMemoTitles({
                    titles: updatedData,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable.pageNumber,
                });
            } catch (error) {
                console.error('Create Title failed:', error);
            } finally {
                setLoadingFalse();
            }
        });
    };

    return (

        <>
            {/* Header Row with Title and Action Buttons */}
            <Row style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20}}>

                <Col>
                    <Typography variant="T50" style={{marginLeft: 20}}>
                        Clearance Memo Titles
                    </Typography>
                </Col>
                <Col style={{display: 'flex', gap: 8}}>
                    <Button
                        iconType="fa-solid fa-plus"
                        labelText="Create"
                        styleType="link"
                        onClick={handleCreateTitle}
                        disabled={!isLegalAdmin}
                    >
                        Create
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={!isLegalAdmin && !isLegalUser && !isProductCommentsAdmin}
                    >
                        Export
                    </Button>
                </Col>
            </Row>
            <TwoRowsLayout>
                {/* Filter Form and Grid Layout */}
                <main data-position="top">
                    <ClearanceMemoTitlesFilterForm/>
                </main>
                <main data-position="bottom">
                    <ClearanceMemoTitlesGrid/>
                </main>
            </TwoRowsLayout>
            {/* Pagination Component */}
            <Pagination
                pageCount={totalPages}
                onPageChange={(e) => handleChange(e.selected)}
                previousLabel="←"
                nextLabel="→"
                forcePage={currentPage}
            />
            <ToastNotificationProvider/>
        </>
    )
        ;
};
